import {
  type HttpErrorResponse,
  type HttpEvent,
  type HttpHandlerFn,
  type HttpInterceptorFn,
  type HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, type Observable, throwError } from 'rxjs';

import { ApiError } from '../models/errors/ApiError';
import { BadRequestError } from '../models/errors/BadRequestError';
import { ConflictError } from '../models/errors/ConflictError';
import { ForbiddenError } from '../models/errors/ForbiddenError';
import { NotFoundError } from '../models/errors/NotFoundError';
import {
  type MissingScoreDto,
  ScoringIncompleteError,
} from '../models/errors/ScoringIncompleteError';
import { UnauthorizedError } from '../models/errors/UnauthorizedError';

import { NotificationService } from './notification.service';

interface ApiErrorBody {
  message?: string;
  errors?: unknown[];
}

/**
 * Checks if the request should be ignored by the interceptor's notification logic.
 */
const shouldIgnoreError = (error: HttpErrorResponse, req: HttpRequest<unknown>): boolean => {
  if (
    req.url.includes('i18n') ||
    req.url.includes('kudos.json') ||
    req.url.includes('/login') ||
    req.url.includes('assets/help/')
  ) {
    return true;
  }

  return false;
};

/**
 * Type guard for ScoringIncompleteError structure.
 */
const isScoringIncomplete = (message: string): boolean => {
  return message === 'Scoring is not complete.';
};

/**
 * Handles 409 Conflict errors, specifically checking for ScoringIncompleteError.
 */
const handleConflictError = (
  message: string,
  errors: unknown[],
  error: HttpErrorResponse,
  notificationService: NotificationService,
): Observable<never> => {
  if (isScoringIncomplete(message)) {
    return throwError(() => new ScoringIncompleteError(message, errors as MissingScoreDto[]));
  }

  notificationService.showError(message, error, {
    translate: false,
    duration: 5000,
    panelClass: ['error-snackbar'],
  });
  return throwError(() => new ConflictError(message, errors));
};

/**
 * Handles default/fallback error cases.
 */
const handleDefaultError = (
  message: string,
  errors: unknown[],
  error: HttpErrorResponse,
  notificationService: NotificationService,
): Observable<never> => {
  if (error.error instanceof ProgressEvent) {
    notificationService.showError('common.networkError', error, {
      duration: 5000,
      panelClass: ['error-snackbar'],
    });
  } else if (message) {
    notificationService.showError(message, error, {
      translate: false,
      duration: 5000,
      panelClass: ['error-snackbar'],
    });
  } else {
    notificationService.showError('common.unexpectedError', error, {
      duration: 5000,
      panelClass: ['error-snackbar'],
    });
  }

  return throwError(() => new ApiError(error.status, message, errors));
};

/**
 * Handles 400 Bad Request errors.
 */
const handleBadRequest = (
  message: string,
  errors: unknown[],
  error: HttpErrorResponse,
  notificationService: NotificationService,
): Observable<never> => {
  notificationService.showError(message, error, {
    translate: false,
    duration: 5000,
    panelClass: ['error-snackbar'],
  });
  return throwError(() => new BadRequestError(message, errors));
};

/**
 * Handles 401 Unauthorized errors.
 */
const handleUnauthorized = (
  message: string,
  errors: unknown[],
  error: HttpErrorResponse,
  notificationService: NotificationService,
): Observable<never> => {
  notificationService.showError('common.unauthorizedError', error, {
    duration: 5000,
    panelClass: ['error-snackbar'],
  });
  return throwError(() => new UnauthorizedError(message, errors));
};

/**
 * Handles 403 Forbidden errors.
 */
const handleForbidden = (
  message: string,
  errors: unknown[],
  error: HttpErrorResponse,
  notificationService: NotificationService,
): Observable<never> => {
  notificationService.showError('common.forbiddenError', error, {
    duration: 5000,
    panelClass: ['error-snackbar'],
  });
  return throwError(() => new ForbiddenError(message, errors));
};

/**
 * Handles 404 Not Found errors.
 */
const handleNotFound = (
  message: string,
  errors: unknown[],
  error: HttpErrorResponse,
  notificationService: NotificationService,
): Observable<never> => {
  notificationService.showError(message, error, {
    translate: false,
    duration: 5000,
    panelClass: ['error-snackbar'],
  });
  return throwError(() => new NotFoundError(message, errors));
};

const handleHttpError = (
  error: HttpErrorResponse,
  req: HttpRequest<unknown>,
  notificationService: NotificationService,
): Observable<never> => {
  const apiError = error.error as ApiErrorBody | undefined;
  const message = apiError?.message ?? error.message ?? 'An unexpected error occurred.';
  const errors = apiError?.errors ?? [];

  if (shouldIgnoreError(error, req)) {
    return throwError(() => new ApiError(error.status, message, errors));
  }

  console.warn('Error caught by interceptor:', error);

  const handlers: Record<
    number,
    (m: string, e: unknown[], err: HttpErrorResponse, ns: NotificationService) => Observable<never>
  > = {
    400: handleBadRequest,
    401: handleUnauthorized,
    403: handleForbidden,
    404: handleNotFound,
    409: handleConflictError,
  };

  const handler = handlers[error.status];
  if (handler) {
    return handler(message, errors, error, notificationService);
  }

  return handleDefaultError(message, errors, error, notificationService);
};

export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const notificationService = inject(NotificationService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      return handleHttpError(error, req, notificationService) as Observable<HttpEvent<unknown>>;
    }),
  );
};
